import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Copy,
  Link2,
  Loader2,
  MonitorUp,
  Play,
  Radio,
  RefreshCw,
  Settings2,
  Square,
  UserX,
  Users,
  Video,
  Volume2,
  VolumeX,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { Avatar } from "../../components/Avatar";
import {
  Badge,
  ConnectionTypeBadge,
  RoomStatusBadge,
} from "../../components/Badge";
import { Button } from "../../components/Button";
import { StatsPanel } from "../../components/StatsPanel";
import { api, errorMessage } from "../../lib/api";
import {
  BITRATE_OPTIONS_KBPS,
  FPS_OPTIONS,
  RESOLUTION_OPTIONS,
  SOURCE_PREFERENCES,
  applyCaptureSettings,
  captureDisplay,
  defaultBitrateKbps,
  measureVideoFps,
  stopMediaStream,
  type CaptureInfo,
  type SourcePreference,
} from "../../lib/capture";
import { cn } from "../../lib/cn";
import {
  formatBitrate,
  formatFps,
  formatResolution,
  plural,
} from "../../lib/format";
import {
  RoomSocket,
  type RoomSocketStatus,
  type ServerMessage,
} from "../../lib/signaling";
import type {
  IceConfigResponse,
  Participant,
  ParticipantConnectionStats,
  ResolutionLabel,
  Room,
  StartStreamInput,
} from "../../lib/types";
import { StreamBroadcaster } from "../../lib/webrtc";

type StreamConfig = {
  resolutionLabel: ResolutionLabel;
  fps: number;
  bitrateKbps: number;
};

function normalizeResolution(
  value: string | null | undefined,
): ResolutionLabel {
  return RESOLUTION_OPTIONS.some((item) => item.label === value)
    ? (value as ResolutionLabel)
    : "1440p";
}

function initialConfig(room: Room): StreamConfig {
  const label = normalizeResolution(room.settings?.resolutionLabel);
  return {
    resolutionLabel: label,
    fps:
      room.settings?.fps &&
      FPS_OPTIONS.includes(room.settings.fps as 15 | 30 | 60)
        ? room.settings.fps
        : 60,
    bitrateKbps:
      room.settings?.bitrateKbps && room.settings.bitrateKbps >= 500
        ? room.settings.bitrateKbps
        : defaultBitrateKbps(label, 60),
  };
}

function buildStreamInput(
  config: StreamConfig,
  info: CaptureInfo,
  measuredFps: number | null,
): StartStreamInput {
  const width = Math.round(info.width ?? 0);
  const height = Math.round(info.height ?? 0);
  if (width < 320 || height < 180) {
    throw new Error(
      "Не удалось определить фактическое разрешение видеотрека. Выберите источник заново.",
    );
  }
  return {
    resolutionLabel: config.resolutionLabel,
    width,
    height,
    fps: Math.max(1, Math.round(measuredFps ?? info.frameRate ?? config.fps)),
    bitrateKbps: config.bitrateKbps,
  };
}

function peerStateLabel(state: RTCPeerConnectionState | undefined): {
  text: string;
  tone: "neutral" | "amber" | "emerald" | "rose";
} {
  switch (state) {
    case "connected":
      return { text: "В эфире", tone: "emerald" };
    case "disconnected":
      return { text: "Восстановление", tone: "amber" };
    case "failed":
      return { text: "Ошибка ICE", tone: "rose" };
    case "closed":
      return { text: "Закрыто", tone: "neutral" };
    case "new":
    case "connecting":
      return { text: "Подключение", tone: "amber" };
    default:
      return { text: "Ожидание", tone: "neutral" };
  }
}

const socketStatusLabel: Record<RoomSocketStatus, string> = {
  connecting: "Подключение к signaling…",
  open: "Signaling подключён",
  reconnecting: "Переподключение signaling…",
  closed: "Signaling отключён",
};

export function HostRoom({
  room,
  onRoomChange,
  onEnded,
}: {
  room: Room;
  onRoomChange: (room: Room) => void;
  onEnded: (room: Room) => void;
}) {
  const [settings, setSettings] = useState<StreamConfig>(() =>
    initialConfig(room),
  );
  const [source, setSource] = useState<SourcePreference>("screen");
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capture, setCapture] = useState<CaptureInfo | null>(null);
  const [measuredFps, setMeasuredFps] = useState<number | null>(null);
  const [viewers, setViewers] = useState<Participant[]>([]);
  const [peerStates, setPeerStates] = useState<
    Record<string, RTCPeerConnectionState>
  >({});
  const [peerStats, setPeerStats] = useState<
    Record<string, ParticipantConnectionStats | undefined>
  >({});
  const [socketStatus, setSocketStatus] =
    useState<RoomSocketStatus>("connecting");
  const [selecting, setSelecting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [selfId, setSelfId] = useState<string | null>(null);

  const previewRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const captureRef = useRef<CaptureInfo | null>(null);
  const settingsRef = useRef(settings);
  const sourceRef = useRef(source);
  const audioRef = useRef(audioEnabled);
  const viewersRef = useRef<Participant[]>([]);
  const roomRef = useRef(room);
  const liveRef = useRef(room.status === "live");
  const socketRef = useRef<RoomSocket | null>(null);
  const broadcasterRef = useRef<StreamBroadcaster | null>(null);
  const iceRef = useRef<IceConfigResponse | null>(null);
  const selectingRef = useRef(false);
  const startingRef = useRef(false);
  const stoppingRef = useRef(false);
  const endedRef = useRef(room.status === "ended");
  const onRoomChangeRef = useRef(onRoomChange);
  const onEndedRef = useRef(onEnded);
  const handleMessageRef = useRef<(message: ServerMessage) => void>(
    () => undefined,
  );
  const stopRef = useRef<(reason: string) => Promise<void>>(
    async () => undefined,
  );
  const copyTimerRef = useRef<number | null>(null);

  settingsRef.current = settings;
  sourceRef.current = source;
  audioRef.current = audioEnabled;
  roomRef.current = room;
  viewersRef.current = viewers;
  onRoomChangeRef.current = onRoomChange;
  onEndedRef.current = onEnded;

  const setViewerList = (next: Participant[]) => {
    viewersRef.current = next;
    setViewers(next);
  };

  const persistStreamSettings = async (info: CaptureInfo): Promise<void> => {
    if (!liveRef.current) return;
    try {
      const input = buildStreamInput(settingsRef.current, info, measuredFps);
      const response = await api.updateStream(roomRef.current.id, input);
      onRoomChangeRef.current(response.room);
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const finishEnded = (endedRoom: Room) => {
    if (endedRef.current) return;
    endedRef.current = true;
    liveRef.current = false;
    broadcasterRef.current?.resetPeers();
    const current = streamRef.current;
    streamRef.current = null;
    captureRef.current = null;
    setStream(null);
    setCapture(null);
    setMeasuredFps(null);
    stopMediaStream(current);
    setNotice(null);
    onEndedRef.current(endedRoom);
  };

  const stopBroadcast = async (reason: string): Promise<void> => {
    if (stoppingRef.current || endedRef.current) return;
    stoppingRef.current = true;
    setStopping(true);
    setError(null);
    try {
      const response = await api.stopRoom(roomRef.current.id);
      finishEnded(response.room);
    } catch (caught) {
      setError(errorMessage(caught));
      if (reason === "capture_ended") {
        setNotice(
          "Захват экрана завершён. Нажмите «Остановить трансляцию», чтобы закрыть комнату.",
        );
      }
    } finally {
      stoppingRef.current = false;
      setStopping(false);
    }
  };
  stopRef.current = stopBroadcast;

  const doCapture = async (): Promise<{
    stream: MediaStream;
    info: CaptureInfo;
  } | null> => {
    if (selectingRef.current) return null;
    selectingRef.current = true;
    setSelecting(true);
    setError(null);
    setNotice(null);
    const config = settingsRef.current;
    try {
      const result = await captureDisplay({
        resolution: config.resolutionLabel,
        fps: config.fps,
        source: sourceRef.current,
        audio: audioRef.current,
      });
      const track = result.stream.getVideoTracks()[0];
      track?.addEventListener("ended", () => {
        if (streamRef.current !== result.stream || endedRef.current) return;
        setError("Захват экрана завершён. Трансляция будет остановлена.");
        void stopRef.current("capture_ended");
      });

      const previous = streamRef.current;
      streamRef.current = result.stream;
      captureRef.current = result.info;
      setStream(result.stream);
      setCapture(result.info);
      setMeasuredFps(null);
      setNotice(
        result.info.warnings.length > 0
          ? "Источник выбран, но браузер не применил часть ограничений."
          : "Источник выбран. Проверьте preview перед запуском.",
      );
      const broadcaster = broadcasterRef.current;
      broadcaster?.setStream(result.stream);
      if (previous && previous !== result.stream) {
        stopMediaStream(previous);
      }
      if (liveRef.current) {
        if (broadcaster) {
          for (const viewer of viewersRef.current) {
            broadcaster.addViewer(viewer.userId);
          }
        }
        await persistStreamSettings(result.info);
      }
      return result;
    } catch (caught) {
      setError(errorMessage(caught));
      return null;
    } finally {
      selectingRef.current = false;
      setSelecting(false);
    }
  };

  const handleStart = async () => {
    if (startingRef.current || endedRef.current) return;
    if (socketStatus !== "open") {
      setError(
        "Signaling-соединение ещё не готово. Подождите пару секунд и попробуйте снова.",
      );
      return;
    }
    startingRef.current = true;
    setStarting(true);
    setError(null);
    setNotice(null);
    try {
      let currentStream = streamRef.current;
      let info = captureRef.current;
      if (!currentStream || !info) {
        const captured = await doCapture();
        if (!captured) return;
        currentStream = captured.stream;
        info = captured.info;
      }
      const input = buildStreamInput(settingsRef.current, info, measuredFps);
      const response = await api.startRoom(roomRef.current.id, input);
      liveRef.current = true;
      onRoomChangeRef.current(response.room);
      const broadcaster = broadcasterRef.current;
      if (broadcaster) {
        broadcaster.setStream(currentStream);
        broadcaster.setBitrate(settingsRef.current.bitrateKbps);
        for (const viewer of viewersRef.current) {
          broadcaster.addViewer(viewer.userId);
        }
      }
      setNotice("Трансляция запущена. Зрители могут подключаться.");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      startingRef.current = false;
      setStarting(false);
    }
  };

  const handleResolutionChange = async (label: ResolutionLabel) => {
    const previous = settingsRef.current;
    const previousDefault = defaultBitrateKbps(
      previous.resolutionLabel,
      previous.fps,
    );
    const next = {
      ...previous,
      resolutionLabel: label,
      bitrateKbps:
        previous.bitrateKbps === previousDefault
          ? defaultBitrateKbps(label, previous.fps)
          : previous.bitrateKbps,
    };
    settingsRef.current = next;
    setSettings(next);
    broadcasterRef.current?.setBitrate(next.bitrateKbps);
    if (!streamRef.current) return;
    try {
      const info = await applyCaptureSettings(
        streamRef.current,
        label,
        next.fps,
      );
      captureRef.current = info;
      setCapture(info);
      await persistStreamSettings(info);
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const handleFpsChange = async (fps: number) => {
    const previous = settingsRef.current;
    const previousDefault = defaultBitrateKbps(
      previous.resolutionLabel,
      previous.fps,
    );
    const next = {
      ...previous,
      fps,
      bitrateKbps:
        previous.bitrateKbps === previousDefault
          ? defaultBitrateKbps(previous.resolutionLabel, fps)
          : previous.bitrateKbps,
    };
    settingsRef.current = next;
    setSettings(next);
    broadcasterRef.current?.setBitrate(next.bitrateKbps);
    if (!streamRef.current) return;
    try {
      const info = await applyCaptureSettings(
        streamRef.current,
        next.resolutionLabel,
        fps,
      );
      captureRef.current = info;
      setCapture(info);
      await persistStreamSettings(info);
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const handleBitrateChange = (bitrateKbps: number) => {
    const next = { ...settingsRef.current, bitrateKbps };
    settingsRef.current = next;
    setSettings(next);
    broadcasterRef.current?.setBitrate(bitrateKbps);
    if (captureRef.current) {
      void persistStreamSettings(captureRef.current);
    }
  };

  const handleAudioChange = () => {
    const next = !audioEnabled;
    setAudioEnabled(next);
    audioRef.current = next;
    if (streamRef.current) {
      setNotice("Чтобы применить настройку звука, выберите источник заново.");
    }
  };

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      if (copyTimerRef.current != null)
        window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setError(
        "Не удалось скопировать ссылку. Скопируйте адрес из строки браузера.",
      );
    }
  };

  // Attach preview stream.
  useEffect(() => {
    const video = previewRef.current;
    if (!video) return;
    video.srcObject = stream;
    if (stream) {
      void video.play().catch(() => undefined);
    }
    return () => {
      if (video.srcObject === stream) video.srcObject = null;
    };
  }, [stream]);

  // Measure actual delivered preview FPS instead of trusting getSettings().
  useEffect(() => {
    if (!stream) {
      setMeasuredFps(null);
      return;
    }
    let cancelled = false;
    const run = async () => {
      const video = previewRef.current;
      if (!video || document.visibilityState === "hidden") return;
      const value = await measureVideoFps(video, 1500);
      if (!cancelled && value != null) setMeasuredFps(value);
    };
    const first = window.setTimeout(() => void run(), 1600);
    const interval = window.setInterval(() => void run(), 4000);
    return () => {
      cancelled = true;
      window.clearTimeout(first);
      window.clearInterval(interval);
    };
  }, [stream]);

  // Initialize signaling and broadcaster.
  useEffect(() => {
    let disposed = false;
    const init = async () => {
      try {
        const ice = await api.iceConfig();
        if (disposed) return;
        iceRef.current = ice;
        const broadcaster = new StreamBroadcaster(room.id, ice, {
          onSignal: (payload) => socketRef.current?.send(payload),
          onPeerState: (viewerId, state) => {
            setPeerStates((previous) => ({ ...previous, [viewerId]: state }));
          },
          onPeerStats: (viewerId, stats) => {
            setPeerStats((previous) => ({ ...previous, [viewerId]: stats }));
          },
          onError: (message) => setError(message),
        });
        const currentStream = streamRef.current;
        if (currentStream) broadcaster.setStream(currentStream);
        if (settingsRef.current.bitrateKbps) {
          broadcaster.setBitrate(settingsRef.current.bitrateKbps);
        }
        broadcasterRef.current = broadcaster;

        const socket = new RoomSocket({
          roomId: room.id,
          onMessage: (message) => handleMessageRef.current(message),
          onStatus: (status) => setSocketStatus(status),
          onFatal: (code, message) => {
            if (!disposed) setError(`${message}${code ? ` (${code})` : ""}`);
          },
        });
        socketRef.current = socket;
        socket.connect();
      } catch (caught) {
        if (!disposed) setError(errorMessage(caught));
      }
    };
    void init();

    return () => {
      disposed = true;
      socketRef.current?.destroy();
      socketRef.current = null;
      broadcasterRef.current?.close();
      broadcasterRef.current = null;
      const current = streamRef.current;
      streamRef.current = null;
      stopMediaStream(current);
      if (copyTimerRef.current != null)
        window.clearTimeout(copyTimerRef.current);
    };
  }, [room.id]);

  // Build latest message handler every render so it sees current callbacks.
  handleMessageRef.current = (message: ServerMessage) => {
    const broadcaster = broadcasterRef.current;
    switch (message.type) {
      case "room:joined": {
        setSelfId(message.selfId);
        const nextRoom: Room = { ...message.room, role: "host" };
        liveRef.current = nextRoom.status === "live";
        onRoomChangeRef.current(nextRoom);
        const viewerList = message.participants.filter(
          (participant) => participant.role === "viewer",
        );
        setViewerList(viewerList);
        if (broadcaster) {
          broadcaster.resetPeers();
          if (streamRef.current) {
            broadcaster.setStream(streamRef.current);
            if (liveRef.current) {
              for (const viewer of viewerList)
                broadcaster.addViewer(viewer.userId);
            }
          }
        }
        break;
      }
      case "room:updated": {
        const nextRoom: Room = { ...message.room, role: "host" };
        liveRef.current = nextRoom.status === "live";
        onRoomChangeRef.current(nextRoom);
        const viewerList = message.participants.filter(
          (participant) => participant.role === "viewer",
        );
        setViewerList(viewerList);
        if (nextRoom.status === "ended") {
          void api
            .getRoom(room.id)
            .then((response) => finishEnded(response.room))
            .catch(() =>
              finishEnded({
                ...nextRoom,
                status: "ended",
                endedAt: new Date().toISOString(),
              }),
            );
          break;
        }
        if (liveRef.current && broadcaster && streamRef.current) {
          for (const viewer of viewerList) broadcaster.addViewer(viewer.userId);
        }
        break;
      }
      case "peer:joined":
        if (message.peer.role === "viewer") {
          const next = viewersRef.current.some(
            (viewer) => viewer.userId === message.peer.userId,
          )
            ? viewersRef.current
            : [...viewersRef.current, message.peer];
          setViewerList(next);
          if (liveRef.current && broadcaster && streamRef.current) {
            broadcaster.addViewer(message.peer.userId);
          }
        }
        break;
      case "peer:left": {
        const next = viewersRef.current.filter(
          (viewer) => viewer.userId !== message.peerId,
        );
        setViewerList(next);
        broadcaster?.removeViewer(message.peerId);
        setPeerStates((previous) => {
          const copy = { ...previous };
          delete copy[message.peerId];
          return copy;
        });
        setPeerStats((previous) => {
          const copy = { ...previous };
          delete copy[message.peerId];
          return copy;
        });
        break;
      }
      case "webrtc:answer":
        broadcaster?.handleAnswer(message.fromUserId, message.sdp);
        break;
      case "webrtc:ice":
        broadcaster?.handleIce(message.fromUserId, message.candidate);
        break;
      case "webrtc:restart-request":
        void broadcaster?.restartPeer(message.fromUserId);
        break;
      case "webrtc:offer":
        break;
      case "room:closed":
        void api
          .getRoom(room.id)
          .then((response) => finishEnded(response.room))
          .catch(() =>
            finishEnded({
              ...roomRef.current,
              status: "ended",
              endedAt: new Date().toISOString(),
            }),
          );
        break;
      case "viewer:kicked":
      case "room:left":
      case "pong":
        break;
      case "error":
        setError(
          `${message.message}${message.code ? ` (${message.code})` : ""}`,
        );
        break;
    }
  };

  useEffect(() => {
    if (room.status === "ended") {
      finishEnded(room);
    }
  }, [room.status]);

  useEffect(() => {
    if (room.status !== "live") return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [room.status]);

  const target = RESOLUTION_OPTIONS.find(
    (item) => item.label === settings.resolutionLabel,
  );
  const isLive = room.status === "live";
  const viewerLimit = room.maxViewers;
  const connectedViewers = viewers.filter(
    (viewer) => peerStates[viewer.userId] === "connected",
  ).length;

  return (
    <div className="pb-10">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to="/dashboard"
            className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-white/[0.07] bg-white/[0.02] text-zinc-500 transition-colors hover:border-white/[0.14] hover:text-zinc-200"
            aria-label="Назад в кабинет"
          >
            <ArrowLeft className="size-4" />
          </Link>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-semibold tracking-[-0.03em] text-white sm:text-2xl">
                {room.title}
              </h1>
              <RoomStatusBadge status={room.status} />
            </div>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-zinc-500">
              <span>Ведущий: {room.owner.displayName}</span>
              <span className="text-zinc-700">·</span>
              <span
                className={cn(
                  "inline-flex items-center gap-1.5",
                  socketStatus === "open"
                    ? "text-emerald-400/80"
                    : socketStatus === "reconnecting" ||
                        socketStatus === "connecting"
                      ? "text-amber-300/80"
                      : "text-rose-300/80",
                )}
              >
                <span className="size-1.5 rounded-full bg-current" />
                {socketStatusLabel[socketStatus]}
              </span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void copyInvite()}
          >
            {copied ? (
              <Check className="size-3.5 text-emerald-300" />
            ) : (
              <Copy className="size-3.5" />
            )}
            {copied ? "Скопировано" : "Ссылка"}
          </Button>
          {isLive ? (
            <Button
              variant="danger"
              size="md"
              onClick={() => void stopBroadcast("host_stopped")}
              loading={stopping}
            >
              {!stopping && <Square className="size-3.5 fill-current" />}
              Остановить
            </Button>
          ) : (
            <Button
              variant="primary"
              size="md"
              onClick={() => void handleStart()}
              loading={starting}
              disabled={selecting || socketStatus !== "open"}
              title={
                socketStatus !== "open"
                  ? "Ждём signaling-соединение"
                  : undefined
              }
            >
              {!starting && <Play className="size-3.5 fill-current" />}
              Начать трансляцию
            </Button>
          )}
        </div>
      </div>

      {error && (
        <Alert tone="error" className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>{error}</span>
            <button
              type="button"
              className="text-[11px] underline decoration-rose-300/30 underline-offset-2 hover:text-white"
              onClick={() => setError(null)}
            >
              Скрыть
            </button>
          </div>
        </Alert>
      )}
      {notice && (
        <Alert tone="success" className="mb-4">
          <div className="flex items-center justify-between gap-3">
            <span>{notice}</span>
            <button
              type="button"
              className="text-[11px] text-emerald-200/60 hover:text-white"
              onClick={() => setNotice(null)}
            >
              Ок
            </button>
          </div>
        </Alert>
      )}
      {isLive && !stream && (
        <Alert
          tone="warning"
          className="mb-4"
          title="Трансляция помечена активной, но локального захвата нет"
        >
          Выберите источник экрана и нажмите «Начать трансляцию». Зрители,
          которые уже подключились, ждут видеопоток.
        </Alert>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 space-y-4">
          <div className="overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0a0c11] shadow-[0_20px_80px_-30px_rgba(0,0,0,0.9)]">
            <div className="relative aspect-video w-full bg-[#06070a]">
              {stream ? (
                <video
                  ref={previewRef}
                  autoPlay
                  muted
                  playsInline
                  aria-label="Предпросмотр захваченного экрана"
                  className="absolute inset-0 size-full object-contain"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
                  <div className="mb-4 flex size-14 items-center justify-center rounded-2xl border border-white/[0.08] bg-white/[0.025]">
                    <MonitorUp
                      className="size-6 text-zinc-500"
                      strokeWidth={1.6}
                    />
                  </div>
                  <p className="text-[15px] font-medium text-zinc-200">
                    Источник ещё не выбран
                  </p>
                  <p className="mt-1.5 max-w-sm text-[12px] leading-relaxed text-zinc-600">
                    Нажмите «Выбрать источник» — откроется системный picker
                    браузера. StreamRoom не выбирает окна за вас.
                  </p>
                  <Button
                    variant="secondary"
                    className="mt-5"
                    onClick={() => void doCapture()}
                    loading={selecting}
                  >
                    {!selecting && <MonitorUp className="size-4" />}
                    Выбрать источник
                  </Button>
                </div>
              )}
              {stream && (
                <div className="pointer-events-none absolute left-3 top-3 flex items-center gap-2">
                  <Badge tone={isLive ? "rose" : "neutral"}>
                    {isLive ? "LIVE" : "PREVIEW"}
                  </Badge>
                  {capture && (
                    <span className="rounded-md border border-black/30 bg-black/50 px-2 py-1 font-mono text-[10px] text-white/80 backdrop-blur">
                      {formatResolution(capture.width, capture.height)}
                      {measuredFps ? ` · ${formatFps(measuredFps)}` : ""}
                    </span>
                  )}
                </div>
              )}
              {stream && (
                <div className="pointer-events-none absolute bottom-3 left-3 right-3 flex flex-wrap items-center justify-between gap-2">
                  <span className="rounded-md border border-black/30 bg-black/50 px-2 py-1 text-[10px] text-white/70 backdrop-blur">
                    {capture?.sourceLabel ?? "Источник"}
                  </span>
                  {viewers.length > 0 && (
                    <span className="inline-flex items-center gap-1.5 rounded-md border border-black/30 bg-black/50 px-2 py-1 text-[10px] text-white/80 backdrop-blur">
                      <Users className="size-3" />
                      {viewers.length}/{viewerLimit}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t border-white/[0.06] bg-white/[0.012] px-4 py-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void doCapture()}
                loading={selecting}
                disabled={starting || stopping}
              >
                {!selecting && <RefreshCw className="size-3.5" />}
                {stream ? "Сменить источник" : "Выбрать источник"}
              </Button>
              <span className="text-[11px] text-zinc-600">
                Окончательный выбор делает пользователь в системном окне
                браузера.
              </span>
              <span className="ml-auto hidden font-mono text-[10px] text-zinc-700 sm:block">
                {selfId ? `peer ${selfId.slice(0, 8)}` : "signaling…"}
              </span>
            </div>
          </div>

          <div className="rounded-2xl border border-white/[0.07] bg-[#0c0e14]/75 p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <Video className="size-4 text-zinc-600" />
                <h2 className="text-[14px] font-medium text-zinc-200">
                  Параметры захвата
                </h2>
              </div>
              <Badge tone={capture ? "emerald" : "neutral"}>
                {capture ? "Источник выбран" : "Нет источника"}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-600">
                  Фактически
                </p>
                <p className="mt-1 font-mono text-[13px] text-zinc-200">
                  {formatResolution(capture?.width, capture?.height)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-600">
                  FPS preview
                </p>
                <p className="mt-1 font-mono text-[13px] text-zinc-200">
                  {measuredFps
                    ? formatFps(measuredFps)
                    : capture
                      ? "измеряется…"
                      : "—"}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-600">
                  Цель
                </p>
                <p className="mt-1 font-mono text-[13px] text-zinc-200">
                  {settings.resolutionLabel === "original"
                    ? "Original"
                    : `${settings.resolutionLabel} · ${settings.fps} FPS`}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-600">
                  Звук
                </p>
                <p className="mt-1 font-mono text-[13px] text-zinc-200">
                  {capture ? (capture.hasAudio ? "захвачен" : "нет") : "—"}
                </p>
              </div>
            </div>
            {capture?.warnings.length ? (
              <div className="mt-4 space-y-2">
                {capture.warnings.map((warning) => (
                  <Alert key={warning} tone="warning">
                    {warning}
                  </Alert>
                ))}
              </div>
            ) : null}
            {target?.width &&
              capture?.width &&
              capture.width < target.width && (
                <p className="mt-3 text-[11px] text-amber-300/80">
                  Фактическое разрешение ниже выбранного. Браузер или источник
                  не поддерживает запрошенный размер.
                </p>
              )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-white/[0.07] bg-[#0c0e14]/75 p-5">
            <div className="mb-4 flex items-center gap-2.5">
              <Settings2 className="size-4 text-zinc-600" />
              <h2 className="text-[14px] font-medium text-zinc-200">
                Настройки видео
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <p className="mb-2 text-[11px] text-zinc-500">
                  Предпочтительный источник
                </p>
                <div className="grid grid-cols-3 gap-1 rounded-xl border border-white/[0.06] bg-black/20 p-1">
                  {SOURCE_PREFERENCES.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => setSource(item.value)}
                      className={cn(
                        "rounded-lg px-2 py-2 text-[11px] transition-colors",
                        source === item.value
                          ? "bg-white/[0.08] text-white"
                          : "text-zinc-500 hover:text-zinc-200",
                      )}
                      title={item.description}
                    >
                      {item.title}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-[10px] leading-relaxed text-zinc-700">
                  Это подсказка браузеру. Итоговый выбор — в системном диалоге.
                </p>
              </div>

              <label className="block">
                <span className="mb-2 block text-[11px] text-zinc-500">
                  Разрешение
                </span>
                <select
                  aria-label="Разрешение"
                  value={settings.resolutionLabel}
                  onChange={(event) =>
                    void handleResolutionChange(
                      event.target.value as ResolutionLabel,
                    )
                  }
                  className="h-10 w-full rounded-xl border border-white/10 bg-[#0c0e14] px-3 text-[13px] text-zinc-200 outline-none transition-colors focus:border-violet-400/40"
                >
                  {RESOLUTION_OPTIONS.map((option) => (
                    <option key={option.label} value={option.label}>
                      {option.name}
                      {option.width
                        ? ` · ${option.width}×${option.height}`
                        : " · как в источнике"}
                    </option>
                  ))}
                </select>
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-2 block text-[11px] text-zinc-500">
                    FPS
                  </span>
                  <select
                    aria-label="FPS"
                    value={settings.fps}
                    onChange={(event) =>
                      void handleFpsChange(Number(event.target.value))
                    }
                    className="h-10 w-full rounded-xl border border-white/10 bg-[#0c0e14] px-3 text-[13px] text-zinc-200 outline-none transition-colors focus:border-violet-400/40"
                  >
                    {FPS_OPTIONS.map((fps) => (
                      <option key={fps} value={fps}>
                        {fps} FPS
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-2 block text-[11px] text-zinc-500">
                    Битрейт
                  </span>
                  <select
                    aria-label="Битрейт"
                    value={settings.bitrateKbps}
                    onChange={(event) =>
                      handleBitrateChange(Number(event.target.value))
                    }
                    className="h-10 w-full rounded-xl border border-white/10 bg-[#0c0e14] px-3 text-[13px] text-zinc-200 outline-none transition-colors focus:border-violet-400/40"
                  >
                    {!BITRATE_OPTIONS_KBPS.some(
                      (kbps) => kbps === settings.bitrateKbps,
                    ) && (
                      <option value={settings.bitrateKbps}>
                        {formatBitrate(settings.bitrateKbps)}
                      </option>
                    )}
                    {BITRATE_OPTIONS_KBPS.map((kbps) => (
                      <option key={kbps} value={kbps}>
                        {formatBitrate(kbps)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <button
                type="button"
                onClick={handleAudioChange}
                className="flex w-full items-center justify-between gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3.5 py-3 text-left transition-colors hover:bg-white/[0.035]"
                role="switch"
                aria-checked={audioEnabled}
              >
                <span className="flex items-center gap-2.5">
                  {audioEnabled ? (
                    <Volume2 className="size-4 text-violet-300" />
                  ) : (
                    <VolumeX className="size-4 text-zinc-600" />
                  )}
                  <span>
                    <span className="block text-[12px] text-zinc-300">
                      Системный звук
                    </span>
                    <span className="block text-[10px] text-zinc-600">
                      Экспериментально, не во всех браузерах
                    </span>
                  </span>
                </span>
                <span
                  className={cn(
                    "relative h-5 w-9 rounded-full border transition-colors",
                    audioEnabled
                      ? "border-violet-400/30 bg-violet-500/60"
                      : "border-white/10 bg-white/[0.06]",
                  )}
                >
                  <span
                    className={cn(
                      "absolute top-0.5 size-3.5 rounded-full bg-white transition-transform",
                      audioEnabled ? "translate-x-[18px]" : "translate-x-0.5",
                    )}
                  />
                </span>
              </button>

              <p className="text-[10px] leading-relaxed text-zinc-700">
                Изменение разрешения и FPS применяется к текущему треку через
                applyConstraints. Фактические значения проверяются через
                getSettings() и preview.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-white/[0.07] bg-[#0c0e14]/75 p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <Users className="size-4 text-zinc-600" />
                <h2 className="text-[14px] font-medium text-zinc-200">
                  Зрители · {viewers.length}/{viewerLimit}
                </h2>
              </div>
              <span className="font-mono text-[10px] text-zinc-600">
                {connectedViewers} в эфире
              </span>
            </div>

            {viewers.length === 0 ? (
              <div className="rounded-xl border border-dashed border-white/[0.07] bg-white/[0.012] px-4 py-7 text-center">
                <Users className="mx-auto size-5 text-zinc-700" />
                <p className="mt-3 text-[12px] text-zinc-400">
                  Зрители ещё не подключились
                </p>
                <p className="mt-1 text-[11px] text-zinc-600">
                  Отправьте ссылку на комнату — подключение появится здесь.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-4"
                  onClick={() => void copyInvite()}
                >
                  <Link2 className="size-3.5" />
                  Скопировать ссылку
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {viewers.map((viewer) => {
                  const state = peerStates[viewer.userId];
                  const label = peerStateLabel(state);
                  const stats = peerStats[viewer.userId] ?? null;
                  return (
                    <div
                      key={viewer.userId}
                      className="rounded-xl border border-white/[0.06] bg-white/[0.018] p-3.5"
                    >
                      <div className="flex items-center gap-2.5">
                        <Avatar name={viewer.displayName} size="sm" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[12px] font-medium text-zinc-200">
                            {viewer.displayName}
                          </p>
                          <p className="mt-0.5 flex items-center gap-1.5 text-[10px] text-zinc-600">
                            <span
                              className={cn(
                                "size-1.5 rounded-full",
                                label.tone === "emerald" && "bg-emerald-400",
                                label.tone === "amber" && "bg-amber-400",
                                label.tone === "rose" && "bg-rose-400",
                                label.tone === "neutral" && "bg-zinc-600",
                              )}
                            />
                            {label.text}
                            {stats?.transport === "relay" && (
                              <>
                                <span className="text-zinc-800">·</span>
                                <ConnectionTypeBadge transport="relay" />
                              </>
                            )}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Отключить зрителя"
                          aria-label={`Отключить ${viewer.displayName}`}
                          disabled={!isLive}
                          onClick={() => {
                            if (
                              !window.confirm(
                                `Отключить зрителя ${viewer.displayName}?`,
                              )
                            )
                              return;
                            socketRef.current?.send({
                              type: "host:kick",
                              roomId: room.id,
                              targetUserId: viewer.userId,
                            });
                          }}
                        >
                          <UserX className="size-3.5 text-zinc-500" />
                        </Button>
                      </div>
                      {stats && (
                        <div className="mt-3 border-t border-white/[0.05] pt-3">
                          <StatsPanel stats={stats} compact />
                        </div>
                      )}
                      {state === "failed" && (
                        <p className="mt-2 flex items-center gap-1.5 text-[10px] text-rose-300/80">
                          <AlertTriangle className="size-3" />
                          ICE не установился, выполняется повторная попытка.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-start gap-2 rounded-xl border border-white/[0.05] bg-white/[0.012] px-3.5 py-3 text-[10px] leading-relaxed text-zinc-600">
            <Radio className="mt-0.5 size-3.5 shrink-0" />
            Видео идёт напрямую зрителям. Если прямой маршрут недоступен,
            браузер использует TURN. Сервер не кодирует и не записывает поток.
          </div>
        </div>
      </div>
    </div>
  );
}
